import {
  type RefObject,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash';

export type UseDimensionsImperativeApi = {
  resizeTable: () => void;
};

/**
 * Keep the Table the height of the parent, minus the header and footer.
 * This is to make sure that the table is scrollable and the header and
 * footer are always visible.
 *
 * @param ref A ref to the container div
 * @returns table height in px or undefined if the ref is not set
 */
export const useTableDimensions: (
  ref: RefObject<Element>,
  apiRef?: RefObject<UseDimensionsImperativeApi>
) => {
  height: number;
  width: number;
} = (ref, apiRef) => {
  // Keep the Table the height of the parent.
  const [tableDimensions, setTableDimensions] = useState<{
    height: number;
    width: number;
  }>({ height: 0, width: 0 });

  const resizeTable = useCallback(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    const { height, width } = node.getBoundingClientRect();
    // Fiddle with the values here subtract the height of the footer and header and filter component
    const titleHeight =
      ref.current
        .getElementsByClassName('ant-table-title')
        .item(0)
        ?.getBoundingClientRect().height ?? 0;
    const headerHeight =
      ref.current
        .getElementsByClassName('ant-table-thead')
        .item(0)
        ?.getBoundingClientRect().height ?? 0;
    const footerHeight =
      ref.current
        .getElementsByClassName('ant-table-footer')
        .item(0)
        ?.getBoundingClientRect().height ?? 0;

    const newHeight =
      height - (footerHeight ?? 0) - (headerHeight ?? 0) - (titleHeight ?? 0);

    setTableDimensions({
      height: newHeight,
      width,
    });
  }, [ref.current?.innerHTML]);

  const resizeTableDebounced = useMemo(
    () =>
      debounce(resizeTable, 100, {
        trailing: true,
        maxWait: 100,
      }),
    [resizeTable]
  );

  useImperativeHandle(apiRef, () => ({
    resizeTable,
  }));

  useLayoutEffect(() => {
    resizeTableDebounced();

    if (!ref.current) {
      return;
    }

    const observer = new ResizeObserver(resizeTableDebounced);
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [resizeTableDebounced]);

  return tableDimensions;
};
